import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ordersApi, productsApi } from '../api/client';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';

// Функция форматирования номера телефона в формате Казахстана: +7 (XXX) XXX-XX-XX
const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 1) return '+7';
  if (phoneNumberLength < 4) return `+7 (${phoneNumber.slice(1, 4)}`;
  if (phoneNumberLength < 7) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}`;
  if (phoneNumberLength < 9) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 9)}`;
  return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 9)}-${phoneNumber.slice(9, 11)}`;
};

const getCleanPhoneNumber = (formattedNumber) => {
  return formattedNumber.replace(/[^\d]/g, '');
};

const Orders = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // 'all', 'preorders', 'orders'
  const [searchQuery, setSearchQuery] = useState(''); // Общий поиск по телефону или товару

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      const response = await ordersApi.getAll();
      return response.data;
    },
  });

  // Загружаем все товары для поиска
  const { data: allProducts } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await productsApi.getAll();
      return response.data;
    },
  });

  // Создаем мапу товаров для быстрого поиска
  const productsMap = useMemo(() => {
    if (!allProducts) return {};
    const map = {};
    allProducts.forEach(product => {
      map[product.id] = product;
    });
    return map;
  }, [allProducts]);

  // Фильтрация заказов
  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    
    let filtered = orders;
    
    // Фильтр по типу заказа
    switch (filter) {
      case 'preorders':
        filtered = filtered.filter(order => order.is_preorder);
        break;
      case 'orders':
        filtered = filtered.filter(order => !order.is_preorder);
        break;
      default:
        break;
    }
    
    // Общий поиск по телефону или товару
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const cleanSearch = getCleanPhoneNumber(searchQuery);
      
      filtered = filtered.filter(order => {
        // Поиск по телефону
        if (order.phone && cleanSearch.length > 0) {
          const cleanPhone = getCleanPhoneNumber(order.phone);
          if (cleanPhone.includes(cleanSearch)) {
            return true;
          }
        }
        
        // Поиск по товару (название или артикул)
        if (order.items && Array.isArray(order.items) && order.items.length > 0) {
          const hasMatchingProduct = order.items.some(item => {
            const product = productsMap[item.product_id];
            if (!product) return false;
            
            const nameMatch = product.name?.toLowerCase().includes(query);
            const itemCodeMatch = product.item_code?.toLowerCase().includes(query);
            
            return nameMatch || itemCodeMatch;
          });
          
          if (hasMatchingProduct) {
            return true;
          }
        }
        
        return false;
      });
    }
    
    // Сортировка по дате (от новых к старым)
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return filtered;
  }, [orders, filter, searchQuery, productsMap]);

  // Группировка заказов по дням
  const groupedOrdersByDay = useMemo(() => {
    if (!filteredOrders || filteredOrders.length === 0) return [];
    
    const groups = {};
    
    filteredOrders.forEach(order => {
      const date = new Date(order.date);
      // Получаем ключ дня в формате YYYY-MM-DD используя локальное время
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dayKey = `${year}-${month}-${day}`;
      
      if (!groups[dayKey]) {
        // Создаем дату начала дня для корректного отображения
        const dayStart = new Date(year, date.getMonth(), date.getDate());
        groups[dayKey] = {
          date: dayStart,
          orders: [],
          totalAmount: 0
        };
      }
      
      groups[dayKey].orders.push(order);
      groups[dayKey].totalAmount += parseFloat(order.total_amount || 0);
    });
    
    // Преобразуем объект в массив и сортируем по дате (от новых к старым)
    return Object.values(groups).sort((a, b) => b.date - a.date);
  }, [filteredOrders]);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center py-8">Загрузка заказов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-red-600">
          Ошибка загрузки заказов
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Заказы</h1>
      </div>

      {/* Поиск */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="🔍 Поиск по номеру телефона, названию товара или артикулу..."
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
              aria-label="Очистить поиск"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Фильтры */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Все
        </button>
        <button
          onClick={() => setFilter('preorders')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'preorders'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Предзаказы
        </button>
        <button
          onClick={() => setFilter('orders')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'orders'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Продажи
        </button>
      </div>

      {groupedOrdersByDay && groupedOrdersByDay.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">
            {searchQuery.trim()
              ? 'Заказы не найдены по указанным критериям'
              : filter === 'all' 
                ? 'Заказов пока нет' 
                : filter === 'preorders' 
                  ? 'Предзаказов пока нет' 
                  : 'Продаж пока нет'}
          </p>
          {searchQuery.trim() && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Очистить поиск
            </button>
          )}
        </div>
      ) : (
        <div>
          {groupedOrdersByDay?.map((dayGroup, dayIndex) => {
            const dayDate = dayGroup.date;
            const dayTotal = dayGroup.totalAmount;
            const orderCount = dayGroup.orders.length;
            
            return (
              <div key={dayDate.toISOString()} className="mb-6">
                {/* Заголовок дня с итоговой суммой */}
                <div className="bg-gray-100 rounded-lg px-3 py-2 mb-3 flex flex-wrap justify-between items-center gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {dayDate.toLocaleDateString('ru-RU', { 
                        day: 'numeric',
                        month: 'long'
                      })}
                    </span>
                    <span className="text-xs text-gray-600">
                      {orderCount} {orderCount === 1 ? 'заказ' : orderCount < 5 ? 'заказа' : 'заказов'}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-blue-600 whitespace-nowrap">
                    Итого: {dayTotal.toFixed(2)} ₸
                  </span>
                </div>
                
                {/* Заказы за день */}
                <div className="space-y-3">
                  {dayGroup.orders.map((order) => {
                    const date = new Date(order.date);
                    const itemCount = order.items?.length || 0;
                    const totalAmount = parseFloat(order.total_amount || 0);
                    const isIssued = order.is_issued === 1 || order.is_issued === true;
                    
                    return (
                      <Card
                        key={order.id}
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {order.is_preorder ? '📋 Предзаказ' : '💰 Продажа'} #{order.id}
                              </h3>
                              {isIssued && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                  Выдан
                                </span>
                              )}
                            </div>
                            {order.customer_name && (
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                {order.customer_name}
                              </p>
                            )}
                            {order.items && order.items.length > 0 && (() => {
                              // 1. Группируем элементы по product_id
                              const groupedItems = Object.values(
                                order.items.reduce((acc, item) => {
                                  if (!acc[item.product_id]) {
                                    acc[item.product_id] = {
                                      product_name: item.product_name,
                                      product_id: item.product_id,
                                      // Создаем массив для хранения деталей (диоптрия + кол-во)
                                      details: [{ dioptry: item.dioptry, qty: Number(item.qty) }]
                                    };
                                  } else {
                                    // Если товар уже есть, просто прокидываем новую диоптрию и её qty в массив
                                    acc[item.product_id].details.push({ 
                                      dioptry: item.dioptry, 
                                      qty: Number(item.qty) 
                                    });
                                  }
                                  return acc;
                                }, {})
                              );

                              // 2. Рендерим сгруппированные данные
                              return (
                                <div className="mb-1">
                                  {groupedItems.map(item => {
                                    // Считаем общее количество товара для финальной приписки
                                    const totalQty = item.details.reduce((sum, d) => sum + d.qty, 0);

                                    return (
                                      <span key={item.product_id} className="text-sm text-gray-600 block">
                                        <b>{item.product_name} </b>
                                        
                                        {/* Выводим диоптрии и их количество в скобках */}
                                        {item.details.map((d, index) => (
                                          <span key={index}>
                                            {d.dioptry ? `[-${d.dioptry}]` : 'без диоптрий'} ({d.qty}шт)
                                            {index < item.details.length - 1 ? ' •' : ''}
                                          </span>
                                        ))}
                                
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            {order.phone && (
                              <p className="text-sm text-gray-600 mb-1">
                                📞 {order.phone}
                              </p>
                            )}
                            <p className="text-sm text-gray-500 mb-2">
                              {order.is_preorder ? 'Создан: ' : ''}
                              {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            {order.issued_date && (
                              <p className="text-sm text-green-600 mb-2">
                                Выдан: {new Date(order.issued_date).toLocaleDateString('ru-RU')} {new Date(order.issued_date).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              <span>{itemCount} {itemCount === 1 ? 'товар' : itemCount < 5 ? 'товара' : 'товаров'}</span>
                              {totalAmount > 0 && (
                                <span className="font-bold text-blue-600">
                                  {totalAmount.toFixed(2)} ₸
                                </span>
                              )}
                            </div>
                            {order.paid_amount !== undefined && parseFloat(order.paid_amount || 0) > 0 && (
                              <div className="mt-2 flex items-center gap-2 text-xs">
                                <span className={`font-semibold ${
                                  parseFloat(order.paid_amount || 0) >= totalAmount
                                    ? 'text-green-600'
                                    : 'text-orange-600'
                                }`}>
                                  Оплачено: {parseFloat(order.paid_amount || 0).toFixed(2)} ₸
                                </span>
                                {parseFloat(order.paid_amount || 0) < totalAmount && (
                                  <span className="text-red-600">
                                    Остаток: {(totalAmount - parseFloat(order.paid_amount || 0)).toFixed(2)} ₸
                                  </span>
                                )}
                                {parseFloat(order.paid_amount || 0) >= totalAmount && (
                                  <span className="text-green-600">✓</span>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="text-blue-600">→</span>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
